/**
 * Painel do órgão — a primeira tela de quem entra (projeto_sistema_obras.md §6.1).
 *
 * Diferente do analítico, não tem filtro nem período: é a leitura de **hoje**,
 * apurada no servidor, do que exige ação e da posição do acervo. Quem precisa
 * recortar por fiscal, empresa ou mês vai aos Relatórios.
 *
 * Dinheiro em centavos; data `yyyy-mm-dd`; frações em [0, 1].
 */

import type { ObraStatus, ReportType, VisitStatus } from '../domain.js';

/**
 * O que o painel abrange.
 *
 * `acervo` é o órgão inteiro, para quem o gere. `carteira` é a parte que cabe
 * ao fiscal: as obras em que ele responde ou auxilia. O fiscal continua
 * alcançando as demais na listagem; o painel só não as soma, porque uma visita
 * vencida de outra pessoa não é pendência dele.
 */
export type PainelScope = 'acervo' | 'carteira';

/** Posição do acervo. Não tem recorte de tempo. */
export interface PainelTotals {
  readonly obras: number;
  readonly byStatus: Readonly<Record<ObraStatus, number>>;
  readonly contractedCents: number;
  readonly paidCents: number;
  /** Contratado menos pago, em todo o acervo. Nunca negativo. */
  readonly remainingCents: number;
  /** O mesmo saldo, só nas obras em curso — o que ainda vai ser pago. */
  readonly aliveRemainingCents: number;
  /** Pago sobre contratado, em [0, 1]. */
  readonly executionRatio: number;
}

/** Contagens do que pede ação, sobre as obras em curso. */
export interface PainelAlerts {
  readonly overdueVisits: number;
  readonly dueSoonVisits: number;
  readonly withoutVisit: number;
  readonly paused: number;
  /** Prazo de contrato vencido sem conclusão. */
  readonly expiredContracts: number;
  /** Execução financeira mais de `SCHEDULE_GAP_THRESHOLD` atrás do prazo. */
  readonly behindSchedule: number;
  readonly withoutFiscal: number;
}

export const PAINEL_REASONS = [
  'visita_vencida',
  'paralisada',
  'contrato_vencido',
  'atrasada',
  'sem_fiscal',
  'sem_visita',
  'visita_vencendo',
] as const;
export type PainelReason = (typeof PAINEL_REASONS)[number];

/** Uma obra que pede ação, com o porquê escrito. */
export interface PainelAttentionRow {
  readonly id: string;
  readonly name: string;
  readonly contractNumber: string | null;
  readonly status: ObraStatus;
  /** A mesma foto do cartão de obras: capa definida, senão a do último relatório. */
  readonly coverUrl: string | null;
  /** O responsável, para a tela agrupar as pendências por fiscal. */
  readonly fiscalId: string | null;
  readonly fiscalName: string | null;
  readonly reasons: readonly PainelReason[];
  /** Dias desde que a visita venceu; nulo quando não venceu. */
  readonly visitOverdueDays: number | null;
  /** Dias desde o início da paralisação em vigor. */
  readonly pausedDays: number | null;
  /** Dias desde o fim do prazo de contrato. */
  readonly contractExpiredDays: number | null;
  /**
   * Pago sobre contratado e prazo decorrido, os dois em [0, 1].
   *
   * É o que permite dizer o atraso por extenso — "pago 48% com 88% do prazo"
   * — em vez de uma diferença em pontos percentuais, que ninguém lê de cabeça.
   */
  readonly executionProgress: number;
  readonly timeProgress: number;
}

/** Um mês da série dos últimos doze. `month` é `yyyy-mm`. */
export interface PainelMonth {
  readonly month: string;
  /** Medições vigentes — sem as estornadas — pela data do ato. */
  readonly measuredCents: number;
  /** Relatórios publicados, pela data da visita. */
  readonly reports: number;
  readonly visits: number;
}

export const PAINEL_EXPIRATION_BANDS = [
  'vencido',
  'ate_30',
  'ate_60',
  'ate_90',
  'depois_de_90',
  'sem_prazo',
] as const;
export type PainelExpirationBand = (typeof PAINEL_EXPIRATION_BANDS)[number];

export const PAINEL_EXPIRATION_LABELS: Record<PainelExpirationBand, string> = {
  vencido: 'Vencidos',
  ate_30: 'Até 30 dias',
  ate_60: '31 a 60 dias',
  ate_90: '61 a 90 dias',
  depois_de_90: 'Mais de 90 dias',
  sem_prazo: 'Sem prazo',
};

/** Obras em curso por horizonte de vencimento do contrato. */
export interface PainelExpiration {
  readonly band: PainelExpirationBand;
  readonly obras: number;
  /** O que ainda falta pagar nessas obras. */
  readonly remainingCents: number;
}

/** A carga de um fiscal responsável, sobre as obras em curso. */
export interface PainelFiscalRow {
  readonly id: string;
  readonly name: string;
  /** Obras em curso em que é o responsável. */
  readonly obras: number;
  readonly overdueVisits: number;
  /** Relatórios publicados nos últimos 30 dias, pela data da visita. */
  readonly recentReports: number;
  /** Data da visita do relatório mais recente que o fiscal publicou. */
  readonly lastVisitAt: string | null;
}

export interface PainelSecretariaRow {
  /** Nulo agrupa as obras sem secretaria demandante. */
  readonly id: string | null;
  readonly name: string;
  readonly acronym: string | null;
  readonly obras: number;
  readonly contractedCents: number;
  readonly paidCents: number;
}

/** Um relatório publicado, como aparece na atividade recente. */
export interface PainelActivity {
  readonly id: string;
  readonly obraId: string;
  readonly obraName: string;
  readonly type: ReportType;
  readonly title: string;
  readonly authorName: string | null;
  readonly visitDate: string;
  readonly publishedAt: string | null;
  readonly amountCents: number | null;
  readonly days: number | null;
  /** O lançamento deste relatório foi estornado depois. */
  readonly reversed: boolean;
}

export interface PainelResult {
  /** A data de hoje no fuso do órgão. Toda contagem de dias parte dela. */
  readonly today: string;
  readonly scope: PainelScope;
  readonly totals: PainelTotals;
  readonly alerts: PainelAlerts;
  /** Obras em curso por situação de visita. */
  readonly visits: Readonly<Record<VisitStatus, number>>;
  /** Da mais grave para a menos; a tela agrupa por `fiscalId`. */
  readonly attention: readonly PainelAttentionRow[];
  readonly months: readonly PainelMonth[];
  readonly expirations: readonly PainelExpiration[];
  /** Todo fiscal responsável por obra em curso, mesmo sem pendência. */
  readonly fiscais: readonly PainelFiscalRow[];
  readonly secretarias: readonly PainelSecretariaRow[];
  readonly activity: readonly PainelActivity[];
}
